// @flow
import React, { useState, useEffect, type ComponentType, Fragment, useContext, useCallback } from 'react';
import nest from 'recompose/nest';
import length from 'ramda/es/length';
import { useTranslation } from 'react-i18next';
import VisibilitySensor from 'react-visibility-sensor';

import RowLayout, { Space } from 'app/common/layouts/RowLayout';
import { CheckoutButton } from 'app/common/layouts/commonShop';
import { HeaderLvl3 } from 'app/common/layouts/markup';
import { DotsLoader } from 'app/common/components/Loader';
// $FlowFixMe
import * as paymentStatuses from 'app/common/actions/shop/order/paymentStatuses';
// $FlowFixMe
import * as shippingStatuses from 'app/common/actions/shop/order/shippingStatuses';
import isProduction from 'app/common/lib/isProduction';

import DateFrom from '../../DateFrom';
import PaymentStatus from '../../PaymentStatus';
import ShippingStatus from '../../ShippingStatus';
import Summary from '../../Summary';
import AdminOrderGoods from './AdminOrderGoods';
import AdminOrderFooter from './AdminOrderFooter';
import IssueModal from './IssueModal';
import OrderData, { OrderContext, type OrderDataProps } from '../../OrderData';
import styles from './AdminOrder.module.scss';;


type Props = {
  id: string,
  number: string,
  date: string,
  noIntroSpace?: boolean,
  timezone: string,
  loading: boolean,
};

const AdminOrder = ({
  id,
  number,
  date,
  noIntroSpace,
  timezone,
  loading: loadingExternal,
}: Props) => {
  const { t } = useTranslation('shop');
  const {
    loading: { order: loadingData },
    content: { order },
    request,
  } = useContext(OrderContext);

  const {
    payment_status: paymentStatus,
    shipping_status: shippingStatus,
    amount_discount: amountDiscount,
    order_items: orderItems,
    total,
  } = order;

  const loading = loadingData || loadingExternal;

  // статус видимости, при появлении происходит запрос данных
  const [visibility, setVisibility] = useState(false);
  // requested - чтоб не загружать данные повторно
  const [requested, setRequested] = useState(false);

  useEffect(() => {
    if (!requested && visibility) {
      request.order();
      setRequested(true);
    }
  }, [visibility]); // eslint-disable-line react-hooks/exhaustive-deps


  const [issueModal, showIssueModal] = useState(false);
  const toggleIssue = useCallback(() => {
    showIssueModal(state => !state);
  }, []);

  // если все заказы выданы, то закрываем окно выдачи
  useEffect(() => {
    if (shippingStatus === shippingStatuses.SHIPPING_STATUS_ISSUED) {
      showIssueModal(false);
    }
  }, [shippingStatus]);

  return (
    <VisibilitySensor onChange={setVisibility} partialVisibility delayedCall>
      <Fragment>
        {!noIntroSpace && <Space />}
        <RowLayout
          className={styles.root}
          single={!noIntroSpace}
          last={noIntroSpace}
          flex
        >
          <div className={styles.content}>
            <HeaderLvl3 className={styles.header}>
              {t('order_in_list_header', { number })}
              <DateFrom date={date} timezone={timezone} />
              {loading && <DotsLoader simple />}
              <PaymentStatus status={paymentStatus} />
              {!isProduction()
                && id
                && <span style={{ fontSize: 10, float: 'right' }}>
                  {'id:'}
&nbsp;
                  {id}
                </span>}
            </HeaderLvl3>

            <ShippingStatus status={shippingStatus} />
            <AdminOrderGoods timezone={timezone} />
            <AdminOrderFooter />

          </div>

          <Summary
            className={styles.summary}
            itemsNumber={length(orderItems)}
            sum={total}
            amountDiscount={amountDiscount}
            total={total}
          >
            <CheckoutButton
              className={styles.button}
              onClick={toggleIssue}
              disabled={
                paymentStatus !== paymentStatuses.PAYMENT_STATUS_PAID
                || !(shippingStatus === shippingStatuses.SHIPPING_STATUS_READY_FOR_ISSUE
                || shippingStatus === shippingStatuses.SHIPPING_STATUS_PARTIALLY_ISSUED)
              }
            >
              {t('issuing_bt')}
            </CheckoutButton>
          </Summary>

        </RowLayout>

        {issueModal
          && <IssueModal
            onClose={toggleIssue}
            timezone={timezone}
          />}

      </Fragment>
    </VisibilitySensor>
  );
};

const Nested: ComponentType<{ ...$Exact<OrderDataProps>, ...Props, }> = nest(OrderData, AdminOrder);
export default Nested;
